/**
 * FRIDGE-853 — how many sourced fitment rows each PDP section shows, in ONE
 * place, because three separate consumers have to agree on the number:
 *
 *   1. `app/products/[slug]/page.tsx` caps the database read at it.
 *   2. `app/products/[slug]/product-client.tsx` renders the rows it gets.
 *   3. The JSON-LD on that same page asserts `isAccessoryOrSparePartFor` /
 *      `isRelatedTo` from the SAME arrays.
 *
 * Schema equals visible is a property of the site, not a coincidence, and it
 * used to be broken here precisely because these numbers were literals sitting
 * apart from each other: the filter side read 24 rows, rendered 12, and put all
 * 24 into the structured data. Fetch cap == render cap == schema cap, or the
 * page asserts a relation a reader cannot see.
 *
 * A cap is not a coverage number. Both sections also carry the exact total of
 * renderable rows (`count: 'exact'`) and say how many they are not showing —
 * see `describeFitmentCoverage` below, which is the one sentence both sides
 * use so the two never word the same fact differently.
 */

/**
 * Compatible filters listed on an APPLIANCE page: one manufacturer cartridge
 * given the hero treatment plus up to seven alternatives. The widest appliance
 * in the table carries 32 renderable filters, so this is a real cap, not a
 * theoretical one.
 */
export const COMPATIBLE_FILTER_CAP = 8

/**
 * Refrigerators listed on a FILTER page. Matches the four-across grid at three
 * rows on desktop, six rows at the two-across mobile layout. The widest
 * cartridge in the table fits 80 renderable appliances.
 */
export const FITS_FRIDGE_CAP = 12

/**
 * The honest sentence for a capped fitment list.
 *
 * `shown` is what the markup actually renders and `total` is the exact count of
 * rows that COULD render — never the raw table count, which includes rows
 * pointing at archived or PDP-gated products that no reader can reach.
 *
 * Returns null when there is nothing worth saying: a list showing everything it
 * has needs no disclosure, and a section with no rows never renders at all
 * (Real-or-Hidden). A `total` below `shown` is treated as "no disclosure"
 * rather than printed, because a page must never claim to show more rows than
 * it counted — that would be a worse lie than silence.
 */
export function describeFitmentCoverage(
  shown: number,
  total: number,
  noun: { one: string; many: string },
): string | null {
  if (shown <= 0) return null
  if (!Number.isFinite(total) || total <= shown) return null
  return `Showing ${shown} of ${total} ${total === 1 ? noun.one : noun.many} we track.`
}
